// CoverVariationsModal.tsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Select,
  Input,
  message,
  Tooltip,
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import AppTour, { renderAppTourPanel } from './tours/AppTour';
import ModalCostBreakdown, { type ModalCostBreakdownRow } from './components/ModalCostBreakdown';
import { imageDataUrlFromBase64 } from './utils/imageMime';
import { resolveFrontendApiBase } from './utils/apiBase';
import { getAuthHeaders } from './utils/authHeaders';
import { coverModelOptions } from './utils/coverModelOptions';
import './CoverModalRefresh.css';
import './CoverVariationsModal.css';

interface CoverVariationsModalProps {
  visible: boolean;
  onClose: () => void;
  baseImage: string; // data URL of the selected image to vary
  targetLanguage: string;
  bookId: string;
  userId: string;
  bookTitle: string;
  translatedTitle?: string;
  seriesName?: string;
  translatedSeriesName?: string;
  authorName?: string;
  generationOptions?: {
    mode: 'modify' | 'generate';
    replaceTitle?: boolean;
    replaceSeries?: boolean;
    addLanguageSticker?: boolean;
    authorMode?: 'include' | 'omit';
    additionalReplacements?: Array<{ source: string; replacement: string }>;
  };
  creditBalance?: { credit_balance: number } | null;
  getCoverCreditCost?: (args: { mode: 'modify' | 'generate'; imageCount: number }) => number;
  onGenerated: (images: string[]) => void;
  // New optional callbacks to enable global spinner UX (close modals + parent handles progress)
  onStart?: (language: string) => void;
  onComplete?: (images: string[]) => void;
}

const API_BASE = resolveFrontendApiBase();

interface ImageModelOption {
  id: string;
  displayName: string;
  apiModel: string;
  provider: 'openai' | 'gemini' | 'openrouter_image' | string;
  enabledModes?: string[];
}
const DEFAULT_VARIATION_IMAGE_MODELS: ImageModelOption[] = [
  { id: 'gpt-image-2', displayName: 'OpenAI Image 2', apiModel: 'gpt-image-2', provider: 'openai' },
  { id: 'google/gemini-3.1-flash-image-preview', displayName: 'Gemini 3.1 Flash Image (Preview)', apiModel: 'google/gemini-3.1-flash-image-preview', provider: 'gemini' },
];

const CoverVariationsModal: React.FC<CoverVariationsModalProps> = ({
  visible,
  onClose,
  baseImage,
  targetLanguage,
  bookId,
  userId,
  bookTitle,
  translatedTitle,
  seriesName,
  translatedSeriesName,
  authorName,
  generationOptions,
  creditBalance,
  getCoverCreditCost,
  onGenerated,
  onStart,
  onComplete,
}) => {
  const [selectedModel, setSelectedModel] = useState<string>('google/gemini-3.1-flash-image-preview');
  const [imageModelOptions, setImageModelOptions] = useState<ImageModelOption[]>(DEFAULT_VARIATION_IMAGE_MODELS);
  const [imageModelOptionsLoading, setImageModelOptionsLoading] = useState<boolean>(false);
  const [imageCount, setImageCount] = useState<number>(1);
  const [extraPrompt, setExtraPrompt] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isCostBreakdownModalVisible, setIsCostBreakdownModalVisible] = useState(false);

  // Ensure loading is reset each time the modal opens to avoid stuck spinner on reopen
  useEffect(() => {
    if (visible) {
      setLoading(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setImageModelOptionsLoading(true);
    fetch(`${API_BASE}/api/image-models?surface=covers`)
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('Failed to load image models'))))
      .then((data: ImageModelOption[]) => {
        if (cancelled) return;
        const models = (Array.isArray(data) && data.length ? data : DEFAULT_VARIATION_IMAGE_MODELS)
          .filter(model => !model.enabledModes || model.enabledModes.includes('variation'));
        setImageModelOptions(models.length ? models : DEFAULT_VARIATION_IMAGE_MODELS);
        setSelectedModel(prev => (
          (models.length ? models : DEFAULT_VARIATION_IMAGE_MODELS).some(model => model.id === prev || model.apiModel === prev)
            ? prev
            : ((models[0] || DEFAULT_VARIATION_IMAGE_MODELS[0])?.id || 'google/gemini-3.1-flash-image-preview')
        ));
      })
      .catch(() => {
        if (!cancelled) setImageModelOptions(DEFAULT_VARIATION_IMAGE_MODELS);
      })
      .finally(() => {
        if (!cancelled) setImageModelOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  const normalizedLanguage = useMemo(
    () =>
      ((targetLanguage === 'Original' ? 'original' : targetLanguage) || 'original').toLowerCase(),
    [targetLanguage]
  );

  // Persist/recover cover context per book+language (titles/series/options)
  const storageKey = useMemo(
    () => `poeai:coverContext:${bookId}:${normalizedLanguage}`,
    [bookId, normalizedLanguage]
  );
  const [persistedContext, setPersistedContext] = useState<any>(null);

  // Load persisted context on open
  useEffect(() => {
    if (!visible) return;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        setPersistedContext(JSON.parse(raw));
      }
    } catch (_e) {}
  }, [visible, storageKey]);

  // Persist latest props while modal is open (merge-safe)
  useEffect(() => {
    if (!visible) return;
    try {
      const prevRaw = sessionStorage.getItem(storageKey);
      const prev = prevRaw ? JSON.parse(prevRaw) : {};
      const mergedOptions = { ...(prev?.generationOptions || {}), ...(generationOptions || {}) };
      const pick = (a?: string, b?: string) => (a && typeof a === 'string' ? (a as string).trim() : a) ? a : ((b && typeof b === 'string' ? (b as string).trim() : b) ? b : undefined);
      const ctx = {
        bookTitle: pick(bookTitle, prev?.bookTitle) || '',
        translatedTitle: pick(translatedTitle, prev?.translatedTitle),
        seriesName: pick(seriesName, prev?.seriesName),
        translatedSeriesName: pick(translatedSeriesName, prev?.translatedSeriesName),
        generationOptions: Object.keys(mergedOptions).length ? mergedOptions : prev?.generationOptions,
      };
      sessionStorage.setItem(storageKey, JSON.stringify(ctx));
      setPersistedContext(ctx);
    } catch (_e) {}
  }, [visible, bookTitle, translatedTitle, seriesName, translatedSeriesName, generationOptions, storageKey]);

  // Effective values from props merged with persisted context; avoid clobber by empty values
  const prefer = (a?: string, b?: string) => (a && typeof a === 'string' ? (a as string).trim() : a) ? a : ((b && typeof b === 'string' ? (b as string).trim() : b) ? b : undefined);
  const effOptions = useMemo(() => {
    const merged = { ...(persistedContext?.generationOptions || {}), ...(generationOptions || {}) };
    return Object.keys(merged).length ? merged : undefined;
  }, [generationOptions, persistedContext]);
  const effBookTitle = prefer(bookTitle, persistedContext?.bookTitle) || '';
  const effTranslatedTitle = prefer(translatedTitle, persistedContext?.translatedTitle);
  const effSeriesName = prefer(seriesName, persistedContext?.seriesName);
  const effTranslatedSeriesName = prefer(translatedSeriesName, persistedContext?.translatedSeriesName);

  // Determine the text we must preserve on the reference image
  const chosenTitle = useMemo(() => {
    const wantTranslated =
      effOptions?.replaceTitle && (effTranslatedTitle?.trim()) && targetLanguage !== 'Original';
    return (wantTranslated ? effTranslatedTitle : effBookTitle) || '';
  }, [effOptions?.replaceTitle, effTranslatedTitle, effBookTitle, targetLanguage]);

  const chosenSeries = useMemo(() => {
    const wantTranslated =
      effOptions?.replaceSeries && (effTranslatedSeriesName?.trim()) && targetLanguage !== 'Original';
    return (wantTranslated ? effTranslatedSeriesName : effSeriesName) || '';
  }, [effOptions?.replaceSeries, effTranslatedSeriesName, effSeriesName, targetLanguage]);

  const keepLanguageSticker = !!effOptions?.addLanguageSticker;
  const selectedModelConfig = useMemo(
    () => imageModelOptions.find(model => model.id === selectedModel || model.apiModel === selectedModel) || DEFAULT_VARIATION_IMAGE_MODELS[0],
    [imageModelOptions, selectedModel]
  );
  const isGermanStickerScenario = useMemo(() => {
    if (!keepLanguageSticker) return false;
    if (!targetLanguage) return false;
    return targetLanguage.toLowerCase().includes('german');
  }, [keepLanguageSticker, targetLanguage]);

  useEffect(() => {
    if (isGermanStickerScenario && selectedModelConfig.provider === 'gemini') {
      setSelectedModel('gpt-image-2');
    }
  }, [isGermanStickerScenario, selectedModelConfig.provider]);

  // Credits
  const BASE_COST_MODIFY = 500;
  const coverCreditCost =
    getCoverCreditCost
      ? getCoverCreditCost({ mode: 'modify', imageCount })
      : (BASE_COST_MODIFY * imageCount);
  const creditBalanceValue = (creditBalance as any)?.available_balance ?? creditBalance?.credit_balance;
  const isCreditInfoLoading = creditBalanceValue == null;
  const hasEnoughCredits = !isCreditInfoLoading && (creditBalanceValue as number) >= coverCreditCost;

  const disabledReasons: string[] = [];
  if (isCreditInfoLoading) {
    disabledReasons.push('Credit balance is still loading.');
  } else if (!hasEnoughCredits) {
    const available = creditBalanceValue ?? 0;
    disabledReasons.push(
      `Insufficient credits. Need ${coverCreditCost}, available ${available}. Reduce the number of images or purchase more credits.`
    );
  }
  if (!baseImage) {
    disabledReasons.push('No base image selected.');
  }
  const selectedModelLabel = selectedModelConfig.displayName || selectedModelConfig.id || selectedModelConfig.apiModel || selectedModel;
  const formatCredits = (value: number) =>
    new Intl.NumberFormat('en-US').format(Math.max(0, Math.round(value)));
  const numericCreditBalance = (() => {
    if (typeof creditBalanceValue === 'number' && Number.isFinite(creditBalanceValue)) {
      return creditBalanceValue;
    }
    if (typeof creditBalanceValue === 'string') {
      const parsed = Number(creditBalanceValue);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  })();
  const primaryFooterBlocker = disabledReasons[0] || '';
  const footerBlockerLabel = primaryFooterBlocker
    ? /insufficient credits/i.test(primaryFooterBlocker)
      ? 'Top up credits or reduce variations'
      : /loading/i.test(primaryFooterBlocker)
        ? 'Credit balance loading'
        : /base image/i.test(primaryFooterBlocker)
          ? 'Select a base image'
          : 'Resolve blockers'
    : '';
  const variationCostRows = useMemo<ModalCostBreakdownRow[]>(() => [
    {
      key: 'cover-variations',
      label: 'Cover variations',
      amountCredits: coverCreditCost,
      explanation: `${imageCount} variation${imageCount > 1 ? 's' : ''} with ${selectedModelLabel}`,
    },
    {
      key: 'reference-cover',
      label: 'Reference cover text',
      displayValue: 'Preserved',
      explanation: 'Title, series, and language sticker text are kept from the selected cover.',
      tone: 'info',
    },
  ], [coverCreditCost, imageCount, selectedModelLabel]);

  const handleGenerate = async (): Promise<void> => {
    if (!baseImage) {
      message.error('No base image selected for variations.');
      return;
    }
    if (!hasEnoughCredits) {
      message.error('You do not have enough credits to generate these variations.');
      return;
    }

    // Signal parent to handle modal closing and show global spinner
    setLoading(true);
    
    // Persist context so CoverSelection can rehydrate on reopen (merge-safe)
    try {
      const prevRaw = sessionStorage.getItem(storageKey);
      const prev = prevRaw ? JSON.parse(prevRaw) : {};
      const mergedOptions = { ...(prev?.generationOptions || {}), ...(effOptions || {}) };
      const pick = (a?: string, b?: string) => (a && typeof a === 'string' ? (a as string).trim() : a) ? a : ((b && typeof b === 'string' ? (b as string).trim() : b) ? b : undefined);
      const ctx = {
        bookTitle: pick(effBookTitle, prev?.bookTitle) || '',
        translatedTitle: pick(effTranslatedTitle, prev?.translatedTitle),
        seriesName: pick(effSeriesName, prev?.seriesName),
        translatedSeriesName: pick(effTranslatedSeriesName, prev?.translatedSeriesName),
        generationOptions: Object.keys(mergedOptions).length ? mergedOptions : prev?.generationOptions,
      };
      sessionStorage.setItem(storageKey, JSON.stringify(ctx));
      setPersistedContext(ctx);
    } catch (_e) {}
    
    // Close this modal first before triggering parent actions
    onClose();
    
    // Use setTimeout to ensure modal closes before parent actions
    setTimeout(() => {
      if (onStart) {
        onStart(normalizedLanguage);
      }
    }, 0);

    try {
      const submissionProvider =
        selectedModelConfig.provider === 'openrouter_image'
          ? 'openrouter_image'
          : (selectedModelConfig.provider === 'openai' ? 'openai' : 'gemini');
      const submissionModel = selectedModelConfig.id || selectedModelConfig.apiModel;

      const payload = {
        mode: 'modify',
        variation: true,
        keepText: {
          title: (chosenTitle || '').trim(),
          series: (chosenSeries || '').trim() || undefined,
          author: (authorName || '').trim() || undefined,
          authorMode: effOptions?.authorMode || ((authorName || '').trim() ? 'include' : 'omit'),
          language: normalizedLanguage,
          keepLanguageSticker,
          additionalReplacements: effOptions?.additionalReplacements || [],
        },
        coverText: {
          title: (chosenTitle || '').trim(),
          series: (chosenSeries || '').trim() || undefined,
          authorMode: effOptions?.authorMode || ((authorName || '').trim() ? 'include' : 'omit'),
        },
        extraPrompt: (extraPrompt || '').trim() || undefined,
        userId,
        bookId,
        language: normalizedLanguage,
        provider: submissionProvider,
        model: submissionModel,
        n: imageCount,
        quality: 'high',
        response_format: 'b64_json',
        reference_images: [baseImage],
        input_fidelity: 'high',
      };

      const response = await fetch(`${API_BASE}/api/generate-cover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify(payload),
      });

      let data: any = null;
      let parsedOk = true;
      try {
        data = await response.json();
      } catch (_e) {
        parsedOk = false;
      }

      if (!response.ok) {
        const errMsg =
          (data && (data.detail || data.message)) ||
          response.statusText ||
          'Request failed';
        if (onComplete) onComplete([]);
        message.error('Failed to generate variations: ' + errMsg);
        return;
      }
      if (!parsedOk || !data) {
        if (onComplete) onComplete([]);
        message.error('Unexpected server response while generating variations. Please try again.');
        return;
      }

      if (data.status === 'success') {
        const resultsArray = Array.isArray(data.result) ? data.result : [data.result];
        const validImages = resultsArray.filter((img: any) => img && img.b64_json);
        if (validImages.length === 0) {
          const diagText =
            resultsArray.length === 1 && (resultsArray[0] as any)?.content
              ? (resultsArray[0] as any).content
              : undefined;
          if (onComplete) onComplete([]);
          message.error({
            content: (
              <div>
                <p>We couldn’t generate variations this time.</p>
                {diagText && (
                  <p style={{ marginTop: 8, fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {diagText}
                  </p>
                )}
              </div>
            ),
            duration: 6,
          });
          return;
        }

        if ((data as any).partial_failure || ((data as any).generated != null && (data as any).requested != null && (data as any).generated < (data as any).requested)) {
          const gen = (data as any).generated ?? validImages.length;
          const req = (data as any).requested ?? imageCount;
          message.warning(`Generated ${gen} of ${req} variation${req > 1 ? 's' : ''}. Credits were charged only for successful generations.`);
        }
        if ((data as any).credit_charge_status === 'failed') {
          message.warning('Variations generated, but we couldn’t charge credits right now. Your balance may update shortly.');
        }

        const generatedImages = validImages.map((img: any) => imageDataUrlFromBase64(img.b64_json));
        const allImages = Array.from(new Set(([baseImage, ...generatedImages].filter(Boolean)))) as string[];

        if (onComplete) onComplete(allImages);
        if (onGenerated) onGenerated(allImages);
    } else if (data.status === 'moderation_error') {
        if (onComplete) onComplete([]);
        message.error({
          content: (
            <div>
              <p>{data.message}</p>
              <p style={{ marginTop: 8, fontSize: '12px', color: 'var(--text-secondary)' }}>
                {data.suggestion}
              </p>
            </div>
          ),
          duration: 8,
        });
      } else if (data.status === 'error') {
        if (onComplete) onComplete([]);
        const msg = data.message || 'Failed to generate variations';
        if (/insufficient credits/i.test(msg)) {
          message.error('Insufficient credits. Reduce the number of images or purchase more credits.');
        } else {
          message.error(msg);
        }
      } else {
        if (onComplete) onComplete([]);
        throw new Error('Unexpected response status: ' + (data as any).status);
      }
    } catch (e: any) {
      if (onComplete) onComplete([]);
      message.error('Error generating variations: ' + (e?.message || String(e)));
    } finally {
      // Reset loading state in case of any errors
      setLoading(false);
    }
  };

  // --------------------- TOUR: refs and server flags ---------------------
  const imageRef = useRef<HTMLDivElement | null>(null);
  const providerGroupRef = useRef<HTMLDivElement | null>(null);
  const countRef = useRef<HTMLDivElement | null>(null);
  const guidanceRef = useRef<HTMLDivElement | null>(null);
  const creditsRef = useRef<HTMLDivElement | null>(null);
  const generateBtnRef = useRef<HTMLButtonElement | null>(null);

  const [tourOpen, setTourOpen] = useState(false);
  const [flagsFetched, setFlagsFetched] = useState(false);
  const [allowAutoOpen, setAllowAutoOpen] = useState(false);

  // Fetch server-side UI flag and decide whether to auto-open tour
  useEffect(() => {
    if (!visible) {
      setTourOpen(false);
      setFlagsFetched(false);
      setAllowAutoOpen(false);
      return;
    }
    if (!userId) return;
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/user/ui-flags?userId=${encodeURIComponent(userId)}`, {
          headers: await getAuthHeaders(),
        });
        let seen = false;
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          const flags = data?.uiFlags || {};
          seen = !!flags['coverVariationsTourSeen'];
        }
        if (mounted) setAllowAutoOpen(!seen);
      } catch (_e) {
        // Non-blocking
      } finally {
        if (mounted) setFlagsFetched(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [visible, userId]);

  // Auto-open tour once conditions are OK
  useEffect(() => {
    if (!visible || !flagsFetched || !allowAutoOpen) return;
    if (loading) return;
    // Defer to ensure refs are attached
    const t = setTimeout(() => setTourOpen(true), 0);
    setAllowAutoOpen(false);
    return () => clearTimeout(t);
  }, [visible, flagsFetched, allowAutoOpen, loading]);

  const handleCloseTour = async () => {
    setTourOpen(false);
    if (!userId) return;
    try {
      await fetch(`${API_BASE}/user/ui-flags/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify({
          userId,
          flags: { coverVariationsTourSeen: true },
        }),
      });
    } catch (_e) {
      // Non-blocking
    }
  };

  const openTourManually = () => setTourOpen(true);

  const mkStep = (refEl: React.RefObject<HTMLElement>, title: string, description: React.ReactNode, placement?: any) => {
    if (!refEl?.current) return null;
    return { title, description, target: refEl.current, placement };
  };

  const steps = [
    mkStep(
      imageRef,
      'Your reference cover',
      'This is the image we start from. Variations preserve the text and layout while exploring new styles.'
    ),
    mkStep(
      providerGroupRef,
      'Choose a generator',
      'Pick the AI provider that will create your variations. Results can differ in style and speed; try the alternate provider when the first set misses the mark.'
    ),
    mkStep(
      countRef,
      'How many to make',
      'Set how many variations to generate in one go.'
    ),
    mkStep(
      guidanceRef,
      'Extra guidance (optional)',
      'Nudge the style with short, visual notes like “cooler palette”, “textured background”, or “dramatic lighting”. Guidance changes style, not cover text; use new generation if the words need to change.'
    ),
    mkStep(
      creditsRef,
      'Credits & cost',
      <>We’ve priced cover generation with <strong>iteration in mind.</strong> Most books take a few rounds to nail typography and layout. See your balance and the estimated total for this run. You’re only charged for successful generations.</>
    ),
    mkStep(
      generateBtnRef as any,
      'Generate variations',
      'Create your images. If the button is disabled, review the blocker list in the modal. After you click, this window closes and progress appears on the next screen.'
    ),
  ].filter(Boolean) as any[];
  const footerCostSummary = (
    <div ref={creditsRef} className="translation-modal-footer-summary cover-refresh-footer-summary cover-variations-footer__summary" aria-live="polite">
      <span>
        <strong>{formatCredits(coverCreditCost)} credits</strong> required
      </span>
      <span className="translation-modal-footer-summary__separator">·</span>
      <span>
        <strong>{numericCreditBalance == null ? 'Loading...' : formatCredits(numericCreditBalance)}</strong>{' '}
        available
      </span>
      <span className="translation-modal-footer-summary__separator">·</span>
      <span>
        {imageCount} variation{imageCount > 1 ? 's' : ''}
      </span>
      {!hasEnoughCredits && !isCreditInfoLoading ? (
        <>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span className="translation-modal-footer-summary__warning">Top-up needed</span>
        </>
      ) : null}
      {footerBlockerLabel ? (
        <>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span className="cover-refresh-footer-blocker">Action needed: {footerBlockerLabel}</span>
        </>
      ) : null}
      <Button
        type="link"
        size="small"
        className="translation-modal-footer-summary__details"
        aria-label="View cost breakdown"
        onClick={() => setIsCostBreakdownModalVisible(true)}
      >
        View details
      </Button>
    </div>
  );

  return (
    <>
      <Modal
        title={
          <div className="cover-refresh-titlebar">
            <span className="cover-refresh-title">Generate Variations</span>
            <Button
              type="link"
              size="small"
              icon={<QuestionCircleOutlined />}
              onClick={openTourManually}
              className="app-tour-trigger cover-refresh-titlebar__tour"
            >
              Take a Tour
            </Button>
          </div>
        }
        visible={visible}
        onCancel={() => !loading && onClose()}
        maskClosable={!loading}
        keyboard={!loading}
        width="min(900px, 92vw)"
        destroyOnClose
        className="cover-variations-modal"
        footer={
          <div className="cover-refresh-footer cover-variations-footer">
            {footerCostSummary}
            <div className="cover-refresh-footer-actions cover-variations-footer__actions">
              <Button key="cancel" onClick={onClose} disabled={loading}>Cancel</Button>
              {disabledReasons.length > 0 ? (
                <Tooltip
                  key="tip"
                  title="Resolve the blockers listed in the modal."
                >
                  <span>
                    <Button key="generate" type="primary" disabled ref={generateBtnRef}>
                      Generate {imageCount} Variation{imageCount > 1 ? 's' : ''}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button key="generate" type="primary" onClick={handleGenerate} loading={loading} ref={generateBtnRef}>
                  Generate {imageCount} Variation{imageCount > 1 ? 's' : ''}
                </Button>
              )}
            </div>
          </div>
        }
        bodyStyle={{ padding: 0 }}
      >
        <div className="cover-refresh-body cover-variations-body">
          {disabledReasons.length > 0 && (
            <div className="cover-variations-blocker-panel" role="status" aria-live="polite">
              <strong>Resolve before generating</strong>
              <ul>
                {disabledReasons.map((reason, index) => (
                  <li key={`${reason}-${index}`}>{reason}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="cover-variations-workflow">
            <section className="cover-refresh-block cover-variations-reference" ref={imageRef}>
              <div className="cover-refresh-block-header">
                <div>
                  <h3>Reference cover</h3>
                  <p>Variation seed and text source.</p>
                </div>
              </div>
              <div className="cover-variations-reference__frame">
                {baseImage ? (
                  <img
                    src={baseImage}
                    alt="Reference cover"
                  />
                ) : (
                  <div className="cover-variations-reference__empty">
                    No image selected
                  </div>
                )}
              </div>
              <div className="cover-variations-reference__note">
                Text on this cover (title/series/language sticker) will be preserved exactly.
              </div>
            </section>

            <section className="cover-refresh-block cover-variations-settings">
              <div className="cover-refresh-block-header">
                <div>
                  <h3>Variation setup</h3>
                  <p>Choose a provider, output count, and optional visual direction.</p>
                </div>
              </div>
              <div className="cover-variations-control-grid">
                <div ref={providerGroupRef} className="cover-refresh-control-tile">
                  <div className="cover-refresh-label">Model</div>
                  <div>
                    <Select
                      value={selectedModel}
                      onChange={(value: string) => setSelectedModel(value)}
                      loading={imageModelOptionsLoading}
                      style={{ width: '100%' }}
                      className="cover-model-select"
                      aria-label="Cover image model"
                      popupClassName="cover-model-dropdown"
                      dropdownMatchSelectWidth={320}
                      dropdownAlign={{ overflow: { adjustX: true, adjustY: true, shiftX: true }, htmlRegion: 'visible' }}
                      optionLabelProp="selectedLabel"
                      options={coverModelOptions(imageModelOptions, model => isGermanStickerScenario && model.provider === 'gemini')}
                    />
                    {isGermanStickerScenario ? (
                      <Tooltip title="Gemini is unavailable while the German Edition sticker is enabled. Choose another model instead.">
                        <div className="cover-variations-model-helper">
                          Gemini is disabled for German sticker variations.
                        </div>
                      </Tooltip>
                    ) : null}
                  </div>
                </div>

                <div ref={countRef} className="cover-refresh-control-tile">
                  <div className="cover-refresh-label">Variations</div>
                  <div>
                    <Select
                      value={imageCount}
                      onChange={(value: number | string) => setImageCount(Number(value))}
                      style={{ width: '100%' }}
                    >
                      <Select.Option value={1}>1 variation</Select.Option>
                      <Select.Option value={2}>2 variations</Select.Option>
                      <Select.Option value={3}>3 variations</Select.Option>
                    </Select>
                  </div>
                </div>
              </div>

              <div ref={guidanceRef} className="cover-refresh-guidance-field cover-variations-guidance">
                <div className="cover-refresh-field-heading">
                  <div className="cover-refresh-label">Extra guidance</div>
                  <Tooltip title="Add short, visual art direction—e.g., 'cooler palette', 'textured background', 'dramatic lighting'. If you want to change the wording, try generating new images instead of variations.">
                    <span className="cover-variations-help-link">What is this?</span>
                  </Tooltip>
                </div>
                <Input.TextArea
                  value={extraPrompt}
                  onChange={e => setExtraPrompt(e.target.value)}
                  autoSize={{ minRows: 4, maxRows: 6 }}
                  placeholder="e.g., cooler palette, subtle texture, stronger rim light, softer shadows"
                  className="cover-refresh-guidance-textarea"
                />
              </div>
            </section>
          </div>

          {/* Guided Tour */}
          <AppTour
            open={tourOpen}
            onClose={handleCloseTour}
            steps={(steps || []).map((s: any) =>
              s
                ? {
                    ...s,
                    description: renderAppTourPanel(s.description, { ravenSize: 'lg' }),
                  }
                : s
            )}
          />
        </div>
      </Modal>
      <Modal
        title="Cost breakdown"
        visible={isCostBreakdownModalVisible}
        onCancel={() => setIsCostBreakdownModalVisible(false)}
        width={560}
        footer={[
          <Button key="close-variation-cost-breakdown" onClick={() => setIsCostBreakdownModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <div data-testid="cover-variation-cost-breakdown-modal">
          <ModalCostBreakdown
            rows={variationCostRows}
            totalCredits={coverCreditCost}
            availableCredits={numericCreditBalance}
            emptyMessage="Select variation options to calculate cost."
            formatCredits={formatCredits}
            footerNote="Credits are charged per successfully generated variation."
          />
        </div>
      </Modal>
    </>
  );
};

export default CoverVariationsModal;
